import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import api from '@/services/api';
import { BaseConnectorMapping, type MappingSection } from './BaseConnectorMapping';
import type { BaseMappingProps } from './index';
import type { SettingspageTranslations } from '@/hooks/useSettingspageTranslations';

interface ShopifyMappingProps extends BaseMappingProps {
  i18n: SettingspageTranslations;
  configId?: number;
}

interface ShopifyChannel {
  id: string;
  name: string;
  published_product_count: number;
  supports_future_publishing: boolean;
}

interface SalesChannel {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

function getShopifyMappingSections(i18n: SettingspageTranslations): MappingSection[] {
  const shopifyConfig = i18n.api.modal.configuration.shopify;

  return [
    {
      title: shopifyConfig.sections.productInformation.title,
      description: shopifyConfig.sections.productInformation.description,
      fields: [
        {
          key: 'body_html_attribute',
          label: shopifyConfig.fields.productDescription.label,
          type: 'attribute',
          filterType: ['text', 'rich_text'],
          description: shopifyConfig.fields.productDescription.description,
          allowDefault: true,
        },
        {
          key: 'vendor_attribute',
          label: shopifyConfig.fields.vendorBrand.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.vendorBrand.description,
          allowDefault: true,
        },
        {
          key: 'product_type_attribute',
          label: shopifyConfig.fields.productType.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.productType.description,
          allowNone: true,
        },
        {
          key: 'tags_attribute',
          label: shopifyConfig.fields.tags.label,
          type: 'attribute',
          filterType: ['text', 'tags'],
          description: shopifyConfig.fields.tags.description,
          allowDefault: true,
        },
      ],
    },
    {
      title: shopifyConfig.sections.inventory.title,
      description: shopifyConfig.sections.inventory.description,
      fields: [
        {
          key: 'inventory.quantity_attribute',
          label: shopifyConfig.fields.stockQuantity.label,
          type: 'attribute',
          filterType: ['number'],
          description: shopifyConfig.fields.stockQuantity.description,
          allowNone: true,
        },
        {
          key: 'inventory.policy_attribute',
          label: shopifyConfig.fields.inventoryPolicy.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.inventoryPolicy.description,
          allowNone: true,
        },
        {
          key: 'inventory.management_attribute',
          label: shopifyConfig.fields.inventoryManagement.label,
          type: 'attribute',
          filterType: ['boolean', 'select'],
          description: shopifyConfig.fields.inventoryManagement.description,
          allowNone: true,
        },
      ],
    },
    {
      title: shopifyConfig.sections.pricing.title,
      description: shopifyConfig.sections.pricing.description,
      fields: [
        {
          key: 'price_type_map.retail',
          label: shopifyConfig.fields.mainPrice.label,
          type: 'price_type',
          description: shopifyConfig.fields.mainPrice.description,
          required: true,
        },
        {
          key: 'price_type_map.compare_at',
          label: shopifyConfig.fields.compareAtPrice.label,
          type: 'price_type',
          description: shopifyConfig.fields.compareAtPrice.description,
          allowNone: true,
        },
      ],
    },
    {
      title: shopifyConfig.sections.productVariants.title,
      description: shopifyConfig.sections.productVariants.description,
      fields: [
        {
          key: 'variants.option1_attribute',
          label: shopifyConfig.fields.option1.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.option1.description,
          allowNone: true,
        },
        {
          key: 'variants.option2_attribute',
          label: shopifyConfig.fields.option2.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.option2.description,
          allowNone: true,
        },
        {
          key: 'variants.option3_attribute',
          label: shopifyConfig.fields.option3.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.option3.description,
          allowNone: true,
        },
        {
          key: 'variants.weight_attribute',
          label: shopifyConfig.fields.weight.label,
          type: 'attribute',
          filterType: ['number', 'measurement'],
          description: shopifyConfig.fields.weight.description,
          allowNone: true,
        },
        {
          key: 'variants.weight_unit_attribute',
          label: shopifyConfig.fields.weightUnit.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          description: shopifyConfig.fields.weightUnit.description,
          allowNone: true,
        },
      ],
    },
    {
      title: shopifyConfig.sections.seoMarketing.title,
      description: shopifyConfig.sections.seoMarketing.description,
      fields: [
        {
          key: 'seo.title_attribute',
          label: shopifyConfig.fields.seoTitle.label,
          type: 'attribute',
          filterType: ['text'],
          description: shopifyConfig.fields.seoTitle.description,
          allowNone: true,
        },
        {
          key: 'seo.description_attribute',
          label: shopifyConfig.fields.seoDescription.label,
          type: 'attribute',
          filterType: ['text', 'rich_text'],
          description: shopifyConfig.fields.seoDescription.description,
          allowNone: true,
        },
        {
          key: 'handle_attribute',
          label: shopifyConfig.fields.urlHandle.label,
          type: 'attribute',
          filterType: ['text'],
          description: shopifyConfig.fields.urlHandle.description,
          allowNone: true,
        },
      ],
    },
  ];
}

function SalesChannelMappingSection({
  form,
  configId,
  i18n,
}: {
  form: any;
  configId: string | number;
  i18n: SettingspageTranslations;
}) {
  const [kernlogicChannels, setKernlogicChannels] = useState<SalesChannel[]>([]);
  const [shopifyChannels, setShopifyChannels] = useState<ShopifyChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch KernLogic sales channels
  const fetchKernlogicChannels = useCallback(async () => {
    try {
      const response = await api.get('/api/channels/');
      const channelsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setKernlogicChannels(channelsData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred';
      setError(`Failed to load channels: ${errorMessage}`);
    }
  }, []);

  // Fetch Shopify channels
  const fetchShopifyChannels = useCallback(async () => {
    if (!configId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/api/external-platform-configs/${configId}/shopify-channels/`
      );
      // eslint-disable-next-line no-console
      console.debug('Shopify channels response', response.data);
      setShopifyChannels(response.data.channels || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred';
      setError(`Failed to load Shopify channels: ${errorMessage}`);
      toast({
        title: 'Error',
        description: `Failed to load Shopify channels: ${errorMessage}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => {
    fetchKernlogicChannels();
    if (configId) {
      fetchShopifyChannels();
    }
  }, [configId, fetchKernlogicChannels, fetchShopifyChannels]);

  const channelMappings = form.watch('settings.mapping.sales_channel_mappings') || {};

  const updateChannelMapping = (kernlogicChannelId: string, shopifyChannelId: string) => {
    const currentMappings = form.getValues('settings.mapping.sales_channel_mappings') || {};

    if (shopifyChannelId === '__none__') {
      // Remove mapping
      const newMappings = { ...currentMappings };
      delete newMappings[kernlogicChannelId];
      form.setValue('settings.mapping.sales_channel_mappings', newMappings);
    } else {
      // Add/update mapping
      form.setValue('settings.mapping.sales_channel_mappings', {
        ...currentMappings,
        [kernlogicChannelId]: shopifyChannelId,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Sales Channel Mapping
          <Button
            variant="outline"
            size="sm"
            onClick={fetchShopifyChannels}
            disabled={loading || !configId}
          >
            <RefreshCw className={`mr-2 size-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Map KernLogic channels to Shopify publications to control where products are visible.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">{error}</div>
        )}

        {kernlogicChannels.length === 0 ? (
          <div className="rounded-md bg-muted p-3 text-sm">
            No KernLogic channels found. Create channels first to map them to Shopify.
          </div>
        ) : (
          kernlogicChannels.map(klChannel => (
            <div key={klChannel.id} className="grid grid-cols-2 items-center gap-4">
              <div>
                <Label className="font-medium">{klChannel.name}</Label>
                <p className="text-xs text-muted-foreground">
                  Code: {klChannel.code} | Status: {klChannel.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div>
                <Select
                  value={channelMappings[klChannel.id]?.toString() || ''}
                  onValueChange={value => updateChannelMapping(klChannel.id.toString(), value)}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Shopify channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No channel (do not publish)</SelectItem>
                    {shopifyChannels.map(shopifyChannel => (
                      <SelectItem key={shopifyChannel.id} value={shopifyChannel.id.toString()}>
                        <div className="flex flex-col">
                          <span>{shopifyChannel.name}</span>
                          <span className="text-xs text-muted-foreground">
                            Published products: {shopifyChannel.published_product_count}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))
        )}

        {shopifyChannels.length === 0 && !loading && !error && (
          <div className="rounded-md bg-muted p-3 text-sm">
            No Shopify sales channels found. Make sure your store has publications configured.
          </div>
        )}

        <div className="border-t pt-2 text-xs text-muted-foreground">
          <p>
            💡 <strong>Tip</strong> Shopify uses publications to control where products are visible.
            Map your KernLogic channels to Shopify publications to sync products to specific sales
            channels.
          </p>
          <ul className="ml-4 mt-1 list-disc space-y-1">
            <li>Sync products to specific Shopify sales channels</li>
            <li>Manage inventory separately per channel</li>
            <li>Control product visibility across different storefronts</li>
          </ul>
          <p className="mt-2">
            <ExternalLink className="mr-1 inline size-3" />
            <a
              href="https://help.shopify.com/en/manual/products/add-update-products/add-products#make-products-available-to-your-sales-channels"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Learn more about Shopify sales channels
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function ShopifyMapping(props: ShopifyMappingProps) {
  const { i18n } = props;
  const configId =
    props.configId ??
    props.form.watch('id') ??
    props.form.getValues('id') ??
    props.form.getValues('configId');
  // eslint-disable-next-line no-console
  console.debug('ShopifyMapping configId', configId);

  return (
    <div className="space-y-6">
      <BaseConnectorMapping {...props} sections={getShopifyMappingSections(i18n)} />
      <SalesChannelMappingSection form={props.form} configId={configId} i18n={i18n} />
    </div>
  );
}
